import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Stethoscope, Store, CalendarDays } from 'lucide-react';

import { useRoleStore } from '@/core/application/state/useRoleStore';
import { useProfileStore } from '@/core/application/state/useProfileStore';
import { useAuthStore } from '@/core/application/security/useAuthStore';
import { GlobalNotificationBell } from '@/core/infrastructure/components/GlobalNotificationBell';
import { useSecretaryStore } from '@/modules/secretary/store/secretaryStore';

export function HubScreen() {
  const navigate = useNavigate();
  const { profile, isClinical: isProfessional } = useProfileStore();
  const { isSecretaria, workspaceIds } = useAuthStore();
  const { setActiveRole } = useRoleStore();

  const handleSelectSecretaria = () => {
    setActiveRole('PROFESSIONAL');
    if (workspaceIds && workspaceIds.length > 0) {
      useSecretaryStore.getState().setActiveWorkspace(workspaceIds[0], 'personal');
    }
    navigate('/secretaria');
  };

  const handleSelectConsumer = () => {
    setActiveRole('CONSUMER');
    navigate('/app');
  };

  const handleSelectProfessional = () => {
    setActiveRole('PROFESSIONAL');
    navigate('/pro');
  };

  return (
    <div className="flex-1 min-h-screen bg-slate-900 p-6 flex flex-col relative">
      <div className="absolute top-6 right-6">
        <GlobalNotificationBell userId={profile?.perfil_usuario_id ?? null} />
      </div>
      <div className="mt-14 mb-10">
        <h1 className="text-[28px] font-extrabold text-white mb-2 m-0">¿Cómo quieres usar Aura hoy?</h1>
        <p className="text-base text-slate-400 leading-6 m-0 mt-2">Selecciona tu espacio de trabajo para continuar.</p>
      </div>

      <div className="flex flex-col gap-6 max-w-md w-full mx-auto md:mx-0">
        {/* Tarjeta Consumidor / Paciente */}
        <button 
          className="p-6 rounded-2xl border border-solid flex flex-col items-start cursor-pointer transition-transform duration-200 text-left w-full bg-white border-slate-200 hover:scale-[1.02]"
          onClick={handleSelectConsumer}
        >
          <div className="w-16 h-16 rounded-2xl bg-yellow-100 flex justify-center items-center mb-4">
            <Search size={32} color="#FFD200" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2 m-0">Directorio Aura</h2>
          <p className="text-[15px] text-slate-600 leading-relaxed m-0">Buscar médicos, agendar citas y gestionar tu salud.</p>
        </button>

        {/* Tarjeta Profesional */}
        {isProfessional && (
          <button 
            className="p-6 rounded-2xl border border-solid flex flex-col items-start cursor-pointer transition-transform duration-200 text-left w-full bg-slate-800 border-slate-700 hover:scale-[1.02]"
            onClick={handleSelectProfessional}
          >
            <div className="w-16 h-16 rounded-2xl bg-blue-100 flex justify-center items-center mb-4">
              <Stethoscope size={32} color="#0052CC" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2 m-0">Mi Consultorio</h2>
            <p className="text-[15px] text-slate-400 leading-relaxed m-0">Gestionar tu perfil, citas y pacientes.</p>
          </button>
        )}

        {/* Tarjeta Secretaría Logística */}
        {isSecretaria && (
          <button 
            className="p-6 rounded-2xl border border-solid flex flex-col items-start cursor-pointer transition-transform duration-200 text-left w-full bg-emerald-50 border-emerald-100 hover:scale-[1.02]"
            onClick={handleSelectSecretaria}
          >
            <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex justify-center items-center mb-4">
              <CalendarDays size={32} className="text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-emerald-900 mb-2 m-0">Panel Logístico</h2>
            <p className="text-[15px] text-emerald-700 leading-relaxed m-0">Revisar citas de pacientes y confirmar pagos.</p>
          </button>
        )}

        {/* Tarjeta Marketplace — Inhabilitada (próximamente) */}
        <div className="p-6 rounded-2xl border border-solid flex flex-col items-start text-left w-full bg-[#1E1435] border-violet-900 relative opacity-50 cursor-default">
          <div className="absolute top-3 right-3 bg-violet-600 py-1 px-2.5 rounded-full">
            <span className="text-[11px] font-bold text-white uppercase tracking-[0.5px]">Próximamente</span>
          </div>
          <div className="w-16 h-16 rounded-2xl bg-violet-100 flex justify-center items-center mb-4">
            <Store size={32} className="text-violet-600" />
          </div>
          <h2 className="text-xl font-bold text-violet-200 mb-2 m-0">Ir de compras</h2>
          <p className="text-[15px] text-violet-600 leading-relaxed m-0">Marketplace de salud: productos, insumos y más.</p>
        </div>
      </div>
    </div>
  );
}
